import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ShoppingBag, X, Loader2, Rocket, Sparkles } from "lucide-react";
import api from "@/utils/api";

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // 'checking', 'success', 'failed'
  const [orderDetails, setOrderDetails] = useState(null);
  const [paymentType, setPaymentType] = useState('purchase'); // 'purchase' or 'sponsored'
  const [sponsoredDetails, setSponsoredDetails] = useState(null);
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const checkPaymentStatus = async () => {
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('session_id');
      const listingId = params.get('listing_id');
      const type = params.get('type');
      const tier = params.get('tier');

      if (!sessionId) {
        setStatus('failed');
        return;
      }

      // Check if this is a sponsored listing payment
      if (type === 'sponsored') {
        setPaymentType('sponsored');
        try {
          // Activate the sponsored listing
          const response = await api.post('/sponsored/activate', {
            session_id: sessionId,
            listing_id: listingId,
            tier: tier || 'basic'
          });

          if (response.data.success) {
            setStatus('success');
            setSponsoredDetails({
              listingId,
              tier: response.data.tier,
              sponsoredId: response.data.sponsored_id,
              expires: response.data.expires
            });
          } else {
            setStatus('failed');
          }
        } catch (error) {
          console.error('Sponsored activation failed:', error);
          setStatus('failed');
        }
        return;
      }

      // Regular purchase payment
      try {
        // Poll for payment status
        let attempts = 0;
        const maxAttempts = 5;
        
        while (attempts < maxAttempts) {
          const response = await api.get(`/payments/stripe/status/${sessionId}`);
          
          if (response.data.payment_status === 'paid') {
            setStatus('success');
            setOrderDetails({
              listingId,
              amount: response.data.amount_total / 100,
              currency: response.data.currency
            });
            return;
          }
          
          if (response.data.status === 'expired') {
            setStatus('failed');
            return;
          }
          
          attempts++;
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
        
        setStatus('failed');
      } catch (error) {
        console.error('Payment status check failed:', error);
        setStatus('failed');
      }
    };

    checkPaymentStatus();
  }, []);

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4 pt-52">
      <Card className="w-full max-w-md shadow-lg">
        <CardContent className="p-8 text-center">
          {status === 'checking' && (
            <>
              <Loader2 className="h-12 w-12 animate-spin text-emerald-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Processing Payment...</h2>
              <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
            </>
          )}
          
          {/* Regular Purchase Success */}
          {status === 'success' && paymentType === 'purchase' && (
            <>
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingBag className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-emerald-600 mb-2">Payment Successful!</h2>
              <p className="text-muted-foreground mb-6">
                Thank you for your purchase. The seller has been notified and will ship your item soon.
              </p>
              {orderDetails && (
                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <p className="text-lg font-semibold">${orderDetails.amount?.toFixed(2)} {orderDetails.currency?.toUpperCase()}</p>
                </div>
              )}
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate('/orders')}>
                  View Orders
                </Button>
                <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={() => navigate('/browse')}>
                  Continue Shopping
                </Button>
              </div>
            </>
          )}

          {/* Sponsored Listing Success */}
          {status === 'success' && paymentType === 'sponsored' && (
            <>
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Rocket className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-emerald-600 mb-2">Listing Boosted! 🚀</h2>
              <p className="text-muted-foreground mb-6">
                Your listing is now promoted and will appear at the top of search results.
              </p>
              {sponsoredDetails && (
                <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-lg p-4 mb-6 border border-emerald-100">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <Sparkles className="h-5 w-5 text-emerald-500" />
                    <span className="font-semibold text-emerald-700">{sponsoredDetails.tier?.name}</span>
                  </div>
                  <p className="text-sm text-gray-600">
                    Active until: {new Date(sponsoredDetails.expires).toLocaleDateString()}
                  </p>
                </div>
              )}
              <div className="flex gap-3 justify-center">
                <Button variant="outline" onClick={() => navigate(`/item/${sponsoredDetails?.listingId}`)}>
                  View Listing
                </Button>
                <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={() => navigate('/dashboard')}>
                  Go to Dashboard
                </Button>
              </div>
            </>
          )}

          {status === 'failed' && (
            <>
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <X className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-red-600 mb-2">Payment Failed</h2>
              <p className="text-muted-foreground mb-6">
                We couldn't confirm your payment. Please try again or contact support.
              </p>
              <Button className="bg-emerald-500 hover:bg-emerald-600" onClick={() => navigate('/browse')}>
                Back to Browse
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccessPage;
